<template>
  <button
    :class="[
      'activity-text-btn',
      sizingClass,
      { 'no-shadow': flat },
      { 'activity-text-btn--focused': focused },
      { 'activity-text-btn--small-squared': smallSquared },
      { 'activity-text-btn--squared': squared },
      { 'activity-text-btn--clue': isClue },
      { 'activity-text-btn--hint': hint },
      { 'activity-text-btn--disabled': disabled || hint },
    ]"
    :style="{
      background: secondaryBgColor || bgColor,
      color,
    }"
    @mousedown="focused = true"
    @mouseup="focused = false"
    @touchstart="focused = true"
    @touchend="focused = false"
    @focus="onFocus"
    @blur="onBlur"
  >
    <i
      v-if="focused && squared"
      class="fas fa-arrows-alt activity-text-btn__icon"
    />
    <span :class="{ 'activity-text-btn__content--focused': focused }">
      <IconVolumeHigh v-if="hasAudio" />
      <slot />
    </span>
  </button>
</template>

<style lang="scss" scoped>
$hintBg: #f4aa45;

.activity-text-btn {
  position: relative;
  padding: 0 13px;
  margin: 0 10px 12px;
  transition: all 0.2s;

  min-height: 20px;
  min-width: 0;

  border-radius: 24px;
  box-shadow: darkgray 3px 3px 4px;

  color: $white100;
  font-weight: $font-weight;
  font-size: 13px;

  display: flex;
  align-items: center;
  justify-content: center;

  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  user-select: none;

  @media (max-width: #{$mobile}px) {
    box-shadow: darkgray 2px 2px 3px;
  }

  &--hint {
    background-color: $hintBg;
  }

  &--disabled {
    pointer-events: none;

    cursor: default;
  }

  &--clue {
    min-width: 60px;
    margin: auto;
  }

  &--medium {
    min-height: 30px;
    padding: 0 20px;
    font-size: 24px;
  }

  &--large {
    min-height: 40px;
    padding: 2px 15px 5px;
    font-size: clamp(24px, 2.1vw, 40px);
  }

  &--small-squared {
    min-height: 39px;
    margin-top: 68px;
    font-size: 26px;
    border-radius: 3px;
  }

  &--squared {
    border-radius: 3px;
  }

  &--focused {
    box-shadow: none;
  }

  &__content--focused {
    opacity: 0.5;
  }

  &__icon {
    position: absolute;
    font-size: 1.2em;
    z-index: 1;
  }
}

.no-shadow {
  box-shadow: none;
}
</style>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { RESOLUTIONS_NAME } from '@/models/constants/resolutions';

import getDefaultSizingClass from '@/utils/helpers/getDefaultSizingClass';
import IconVolumeHigh from '@/components/Icon/IconVolumeHigh.vue';

type Size = 'small' | 'medium' | 'large';

const bgColorTypes = {
  audio: {
    selected: '#f8b6d3',
    default: '#e90c6e',
  },
  draggable: {
    selected: '#f8b6d3',
    default: '#e90c6e',
  },
};

@Component({
  components: { IconVolumeHigh },
})
export default class ActivityTextButton extends Vue {
  private sizingClass = '';
  private secondaryBgColor = '';

  // Old prop used by speaking activities that still have old styling
  @Prop({ default: false })
  private smallSquared!: boolean;

  @Prop({ default: false })
  private squared!: boolean;

  @Prop({ default: false })
  private flat!: boolean;

  @Prop({ default: false })
  private hasAudio!: boolean;

  @Prop({ default: 'white' })
  private color!: string;

  @Prop({ default: '#1b75bb' })
  private bgColor!: string;

  @Prop({ default: '' })
  private bgColorType!: 'audio' | 'draggable';

  @Prop({ default: false })
  private isClue!: boolean;

  @Prop({ default: false })
  private disabled!: boolean;

  @Prop({ default: false })
  private hint!: boolean;

  @Prop({ default: RESOLUTIONS_NAME.MOBILE })
  private mobileSize!: Size;

  @Prop({ default: RESOLUTIONS_NAME.TABLET })
  private tabletSize!: Size;

  @Prop({ default: RESOLUTIONS_NAME.DESKTOP })
  private desktopSize!: Size;

  public focused = false;

  private onResize() {
    this.sizingClass = `activity-text-btn--${getDefaultSizingClass(
      this.desktopSize,
      this.tabletSize,
      this.mobileSize,
    )}
    `;
  }

  private onFocus() {
    if (this.bgColorType) {
      this.secondaryBgColor = bgColorTypes[this.bgColorType].selected;
    }
  }

  private onBlur() {
    if (this.bgColorType) {
      this.secondaryBgColor = bgColorTypes[this.bgColorType].default;
    }
  }

  private mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  private created() {
    if (this.bgColorType) {
      this.secondaryBgColor = bgColorTypes[this.bgColorType].default;
    }
  }

  private destroyed() {
    window.removeEventListener('resize', this.onResize);
  }
}
</script>

<docs>
Text button component (responsive)

### default

```jsx
<ActivityTextButton>Button text</ActivityTextButton>
```

### squared

```jsx
<ActivityTextButton :squared="true">Button text</ActivityTextButton>
```

### custom colors

```jsx
<ActivityTextButton
  color="white"
  bg-color="red"
>
  Button text
</ActivityTextButton>
```

### clue

```jsx
<ActivityTextButton
  :is-clue="true"
>
  Button text
</ActivityTextButton>
```

### custom resolutions sizes

### small

```jsx
<ActivityTextButton desktop-size="small">
  Button text
</ActivityTextButton>
```

### medium

```jsx
<ActivityTextButton desktop-size="medium">
  Button text
</ActivityTextButton>
```

### large

```jsx
<ActivityTextButton desktop-size="large">
  Button text
</ActivityTextButton>
```
</docs>
